import Box, { BoxProps } from '@liquidweb-dev/undercurrent/Box';
import CornerLeftUpIcon from '@liquidweb-dev/undercurrent/icons/lucide/CornerLeftUp';
import FolderIcon from '@liquidweb-dev/undercurrent/icons/lucide/Folder';
import GlobeIcon from '@liquidweb-dev/undercurrent/icons/lucide/Globe';
import Link from '@liquidweb-dev/undercurrent/Link';
import { TypographyProps } from '@liquidweb-dev/undercurrent/Typography';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';
import TypographyOverflow from '@liquidweb-dev/undercurrent/TypographyOverflow';
import { LWApiPublicNotificationDetailsResultRawI } from '@liquidweb-dev/club-sauce/public/notifications/index.raw';
import ApplicationIcon from 'components/ApplicationIcon';
import DisplayTypeIcon from 'components/DisplayTypeIcon';
import ServerStatusBadgeFlat from 'components/ServerStatusBadgeFlat';
import type { AssetDetails } from 'modules/queries/asset/useDetails';
import DetermineCountryFlag from 'components/CountryFlag';
import { Link as RouterLink } from 'react-router-dom';

const AttributeWrapper = ({ sx, ...rest }: BoxProps) => (
	<Box
		sx={combineSx(
			{ display: 'flex', alignItems: 'center', gap: 50, minWidth: 0 },
			sx,
		)}
		{...rest}
	/>
);

const AttributeTypography = ({ sx, ...rest }: TypographyProps) => (
	<TypographyOverflow
		variant="label-lg"
		sx={combineSx({ fontWeight: 500, whiteSpace: 'nowrap' }, sx)}
		{...rest}
	/>
);

export interface AttributesContentProps extends AssetDetails {
	notification?: LWApiPublicNotificationDetailsResultRawI;
	showLocation?: boolean;
	/** prefer displaying `ip` instead of `domain` */
	showIp?: boolean;
	showStatusBadge?: boolean;
}

const AttributesContent = ({
	displayType,
	domain,
	powerStatus,
	privateParent,
	project_name: projectName,
	status,
	templateApp,
	isNutanixPrivateCloud,
	notification,
	country,
	location,
	showLocation,
	ip,
	showIp,
	showStatusBadge = true,
}: AttributesContentProps) => {
	const privateParentName = privateParent?.custom_name || privateParent?.domain;
	const privateParentPath = `/servers/${privateParent?.uniq_id}`;
	const { last_alert: lastAlert, description } = notification || {};
	const showCountry = showLocation && country && location;

	return (
		<>
			{showStatusBadge && (
				<ServerStatusBadgeFlat
					status={status}
					powerStatus={powerStatus}
					notificationPrimary={description}
					notificationSecondary={lastAlert || ''}
				/>
			)}
			{showCountry && (
				<AttributeWrapper>
					<DetermineCountryFlag
						country={country}
						sx={({ size }) => ({
							height: size.xS,
							width: size.xS,
							flexShrink: 0,
						})}
					/>
					<AttributeTypography>{location}</AttributeTypography>
				</AttributeWrapper>
			)}
			<AttributeWrapper>
				<GlobeIcon
					sx={({ vars, size }) => ({
						color: vars.palette.uc.icon.weak,
						height: size.xS,
						width: size.xS,
						flexShrink: 0,
					})}
				/>
				<AttributeTypography>{showIp ? ip : domain}</AttributeTypography>
			</AttributeWrapper>
			{templateApp && !isNutanixPrivateCloud && (
				<AttributeWrapper>
					<ApplicationIcon
						application={templateApp}
						sx={({ vars, size }) => ({
							color: vars.palette.uc.icon.weak,
							height: size.xS,
							width: size.xS,
							flexShrink: 0,
						})}
					/>
					<AttributeTypography>{templateApp}</AttributeTypography>
				</AttributeWrapper>
			)}
			{displayType && (
				<AttributeWrapper>
					<DisplayTypeIcon
						displayType={displayType}
						sx={({ vars, size }) => ({
							color: vars.palette.uc.icon.weak,
							height: size.xS,
							width: size.xS,
							flexShrink: 0,
						})}
					/>
					<AttributeTypography>{displayType}</AttributeTypography>
				</AttributeWrapper>
			)}
			{projectName && (
				<AttributeWrapper>
					<FolderIcon
						sx={({ vars, size }) => ({
							color: vars.palette.uc.icon.weak,
							height: size.xS,
							width: size.xS,
							flexShrink: 0,
						})}
					/>
					<AttributeTypography>{projectName}</AttributeTypography>
				</AttributeWrapper>
			)}
			{privateParentName && (
				<AttributeWrapper
					sx={{
						borderLeft: '1px solid',
						borderColor: ({ vars }) => vars.palette.uc.border.weak,
						alignSelf: 'stretch',
						paddingLeft: 175, // 14px - intentional to give appearance that border is centered,
					}}
				>
					<CornerLeftUpIcon
						sx={({ vars, size }) => ({
							color: vars.palette.uc.icon.weak,
							height: size.xS,
							width: size.xS,
							flexShrink: 0,
						})}
					/>
					<TypographyOverflow>
						<Link
							component={RouterLink}
							to={privateParentPath}
							variant="label-lg"
							sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}
						>
							{privateParentName}
						</Link>
					</TypographyOverflow>
				</AttributeWrapper>
			)}
		</>
	);
};

export default AttributesContent;
